import { area } from '@turf/area';
import { booleanValid } from '@turf/boolean-valid';
import { kinks } from '@turf/kinks';
import { Audience, MapBounds } from '@types';
import dayjs, { Dayjs } from 'dayjs';
import { Feature, FeatureCollection, GeoJsonObject, MultiPolygon, Polygon, Position } from 'geojson';
import type { LngLat } from 'react-map-gl/mapbox';
import { v4 as uuidv4 } from 'uuid';
import { parseWKTPolygons } from './Map/utils';

type WktCoordinate = [number, number];

const toWktCoordinate = (position: Position, label: string): WktCoordinate => {
  const [longitude, latitude] = position;
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    throw new Error(`GeoJSON ${label} must include finite longitude and latitude values`);
  }

  return [longitude, latitude];
};

const normalizePolygonCoordinates = (coordinates: Position[][], label: string): WktCoordinate[][] => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    throw new Error(`GeoJSON ${label} must contain at least one ring`);
  }

  return coordinates.map((ring, ringIndex) => {
    if (!Array.isArray(ring) || ring.length < 4) {
      throw new Error(`GeoJSON ${label} ring ${ringIndex + 1} must contain at least 4 positions`);
    }

    const normalizedRing = ring.map((position, positionIndex) => toWktCoordinate(position, `${label} ring ${ringIndex + 1} position ${positionIndex + 1}`));
    const [firstLongitude, firstLatitude] = normalizedRing[0];
    const [lastLongitude, lastLatitude] = normalizedRing[normalizedRing.length - 1];

    if (firstLongitude !== lastLongitude || firstLatitude !== lastLatitude) {
      normalizedRing.push([firstLongitude, firstLatitude]);
    }

    return normalizedRing;
  });
};

const collectPolygonCoordinates = (geoJson: GeoJsonObject): WktCoordinate[][][] => {
  switch (geoJson.type) {
    case 'Feature': {
      const feature = geoJson as Feature;
      if (!feature.geometry) {
        throw new Error('GeoJSON feature must include a geometry');
      }

      return collectPolygonCoordinates(feature.geometry);
    }
    case 'FeatureCollection': {
      const featureCollection = geoJson as FeatureCollection;
      if (!Array.isArray(featureCollection.features) || featureCollection.features.length === 0) {
        throw new Error('GeoJSON feature collection must include at least one feature');
      }

      return featureCollection.features.flatMap((feature: Feature, index: number) => {
        if (!feature.geometry) {
          throw new Error(`GeoJSON feature ${index + 1} must include a geometry`);
        }

        return collectPolygonCoordinates(feature.geometry);
      });
    }
    case 'Polygon':
      return [normalizePolygonCoordinates((geoJson as Polygon).coordinates, 'polygon')];
    case 'MultiPolygon': {
      const multiPolygon = geoJson as MultiPolygon;
      if (!Array.isArray(multiPolygon.coordinates) || multiPolygon.coordinates.length === 0) {
        throw new Error('GeoJSON multipolygon must contain at least one polygon');
      }

      return multiPolygon.coordinates.map((polygonCoordinates, index) => normalizePolygonCoordinates(polygonCoordinates, `multipolygon polygon ${index + 1}`));
    }
    default:
      throw new Error('Only Polygon and MultiPolygon GeoJSON are supported');
  }
};

const polygonCoordinatesToWkt = (coordinates: WktCoordinate[][]) => {
  return `(${coordinates.map((ring) => `(${ring.map(([longitude, latitude]) => `${longitude} ${latitude}`).join(', ')})`).join(', ')})`;
};

export const geoJsonTextToWkt = (geoJsonText: string): string => {
  let parsedGeoJson: GeoJsonObject;

  try {
    parsedGeoJson = JSON.parse(geoJsonText) as GeoJsonObject;
  } catch {
    throw new Error('GeoJSON must be valid JSON');
  }

  if (!parsedGeoJson || typeof parsedGeoJson !== 'object' || typeof parsedGeoJson.type !== 'string') {
    throw new Error('GeoJSON must be a valid GeoJSON object');
  }

  const polygons = collectPolygonCoordinates(parsedGeoJson);
  const geometry: Polygon | MultiPolygon = polygons.length === 1 ? { type: 'Polygon', coordinates: polygons[0] } : { type: 'MultiPolygon', coordinates: polygons };

  const feature: Feature<Polygon | MultiPolygon> = {
    type: 'Feature',
    properties: {},
    geometry
  };

  if (!booleanValid(feature)) {
    throw new Error('GeoJSON must define a valid polygon');
  }

  if (kinks(feature).features.length > 0) {
    throw new Error('GeoJSON polygon cannot self-intersect');
  }

  return polygons.length === 1
    ? `POLYGON${polygonCoordinatesToWkt(polygons[0])}`
    : `MULTIPOLYGON(${polygons.map((polygonCoordinates) => polygonCoordinatesToWkt(polygonCoordinates)).join(', ')})`;
};

/** Merges a list of POLYGON/MULTIPOLYGON WKT strings into a single WKT geometry, or null if none are parseable. */
export const combineWktGeometries = (wktList: string[]): string | null => {
  const polygons = wktList.flatMap((wkt) => parseWKTPolygons(wkt) || []);

  if (polygons.length === 0) {
    return null;
  }

  return polygons.length === 1
    ? `POLYGON${polygonCoordinatesToWkt(polygons[0])}`
    : `MULTIPOLYGON(${polygons.map((polygonCoordinates) => polygonCoordinatesToWkt(polygonCoordinates)).join(', ')})`;
};

/** Returns a name that doesn't collide with existingNames, appending " (1)", " (2)", etc. as needed.
 * Always uses one above the highest existing counter for the same base name. */
export const makeUniqueName = (baseName: string, existingNames: (string | null | undefined)[]): string => {
  const stripped = baseName.replace(/ \(\d+\)$/, '');
  if (!existingNames.includes(stripped)) {
    return stripped;
  }
  // Find the highest existing counter for this base name
  let highest = 0;
  for (const name of existingNames) {
    if (!name) continue;
    const match = name.match(new RegExp(`^${stripped.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\((\\d+)\\)$`));
    if (match) {
      highest = Math.max(highest, parseInt(match[1], 10));
    }
  }
  return `${stripped} (${highest + 1})`;
};

// Function overload signatures
export function hexToRgb(hex: string): [number, number, number];
export function hexToRgb(hex: string, alpha: number): [number, number, number, number];

// Function implementation
export function hexToRgb(hex: string, alpha?: number): [number, number, number] | [number, number, number, number] {
  // Remove the hash at the start if it's there
  hex = hex.trim(); // Removes any leading or trailing whitespace
  if (hex.startsWith('#')) {
    hex = hex.slice(1); // Removes leading '#'
  }

  // If the string is a shorthand three digit hex code, convert it to six digits
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(function (hex) {
        return hex + hex;
      })
      .join('');
  }

  // Convert to RGB values
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  if (alpha !== undefined) {
    return [r, g, b, alpha];
  } else {
    return [r, g, b];
  }
}

export const hexToRgbString = (hex: string, alpha?: number) => {
  return 'rgba(' + hexToRgb(hex).join(', ') + (alpha !== undefined ? ', ' + alpha : '') + ')';
};

/**
 * Calculate distance between two points using Haversine formula
 * @returns distance in meters
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate total distance of a path with multiple points
 * @returns total distance in meters
 */
export function calculatePathDistance(coords: LngLat[]): number {
  if (coords.length < 2) return 0;

  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    total += calculateDistance(coords[i].lat, coords[i].lng, coords[i + 1].lat, coords[i + 1].lng);
  }
  return total;
}

/**
 * Creates a composite key from wifi_bssid and cluster_id for unique WiFi identification
 */
// Splits a list of IDs on commas, spaces, tabs and newlines, so lists pasted from Excel work
export const parseIdList = (input: string) => input.split(/[\s,]+/).filter(Boolean);

/**
 * The box's text after pasting a list of IDs over the selection from start to end. A list pasted one ID
 * per line (e.g. a column copied from Excel) is rewritten as "a, b, c" and joined to the text around it
 * with commas. Returns null for any other paste, so the browser pastes it as usual.
 */
export const pasteIdListAsCsv = (value: string, start: number, end: number, pasted: string): null | string => {
  if (!/[\r\n\t]/.test(pasted)) return null;
  const ids = parseIdList(pasted).join(', ');
  const before = value.slice(0, start).replace(/[\s,]+$/, '');
  const after = value.slice(end).replace(/^[\s,]+/, '');
  return [before, ids, after].filter(Boolean).join(', ');
};

export const getWifiCompositeKey = (wifi_bssid: string, cluster_id: string): string => {
  return `${wifi_bssid}||${cluster_id}`;
};

export const getBoundsOfWktPolygon = (wkt: string): null | [number, number, number, number] => {
  // Check if the input is a valid WKT Polygon string
  if (!wkt.startsWith('POLYGON') || !wkt.endsWith('))')) {
    return null;
  }

  // Extract the coordinates string and split into coordinate pairs
  const si = wkt.indexOf('(');
  const ei = wkt.indexOf(')');
  const coordinatesString = wkt.slice(si + 2, ei);
  const coordinatePairs = coordinatesString.split(',').map((d) => d.trim());
  // Initialize bounds
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  // Iterate over each coordinate pair to find the min and max of x and y
  coordinatePairs.forEach((pair) => {
    const [x, y] = pair.split(' ').map(Number);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });
  // Return the bounds as an object with minX, minY, maxX, maxY
  return [minX, minY, maxX, maxY];
};

export const calculateMonthsElapsed = (startDate: string, endDate: string) => {
  // Parse the start and end dates to Date objects if they are not already
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Calculate the difference in months and years
  let months = (end.getFullYear() - start.getFullYear()) * 12;
  months -= start.getMonth();
  months += end.getMonth();

  // Adjust if the end day is earlier in the month than the start day
  if (end.getDate() < start.getDate()) {
    months--;
  }

  return months;
};

export const secondsToHumanReadable = (seconds: number): string => {
  // Define the duration of each time unit in seconds
  const day = 86400; // 60 * 60 * 24
  const hour = 3600; // 60 * 60
  const minute = 60;

  // Calculate the days, hours, minutes, and remaining seconds
  const days = Math.floor(seconds / day);
  const hours = Math.floor((seconds % day) / hour);
  const minutes = Math.floor((seconds % hour) / minute);
  const remainingSeconds = Math.round(seconds % minute);

  // Build the output string with colon separators
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0) parts.push(`${remainingSeconds}s`);

  return parts.length > 0 ? parts.join(':') : '0s';
};

export const parseBoundingBox = (boundingBoxString: string) => {
  // Remove 'BOX(' and ')' from the string
  const coordinatesString = boundingBoxString.replace('BOX(', '').replace(')', '');
  // Split the coordinates string into an array of strings
  const coordinatesArray = coordinatesString.split(/[,\s]+/);

  // Parse the strings to numbers
  const minLon = parseFloat(coordinatesArray[0]);
  const minLat = parseFloat(coordinatesArray[1]);
  const maxLon = parseFloat(coordinatesArray[2]);
  const maxLat = parseFloat(coordinatesArray[3]);

  return { minLon, minLat, maxLon, maxLat };
};

export const findClosest = <T>(arr: { value: number }[], x: number): T => {
  // Use reduce to find the object with the closest value to x
  return arr.reduce((closest, current) => {
    // Compare the absolute difference between x and the current value
    return Math.abs(current.value - x) < Math.abs(closest.value - x) ? current : closest;
  }, arr[0]) as T;
};

export const getMinMax = (arr: { record_created_epoch: number }[]) => {
  if (arr.length === 0) return {}; // Handle empty array case

  let min = arr[0].record_created_epoch;
  let max = arr[0].record_created_epoch;

  for (let i = 1; i < arr.length; i++) {
    const currentValue = arr[i].record_created_epoch;

    if (currentValue < min) {
      min = currentValue;
    }
    if (currentValue > max) {
      max = currentValue;
    }
  }

  return { min, max };
};

export const findMissingEpochs = (dates: number[], startEpoch: number, endEpoch: number) => {
  const oneDayInMillis = 24 * 60 * 60 * 1000; // Number of milliseconds in a day

  // Sort the input array of epochs
  const sortedEpochs = dates.sort((a, b) => a - b);

  let currentEpoch = startEpoch;
  const missingEpochs = [];

  // Loop through the sorted epochs and check for missing days
  for (let i = 0; i < sortedEpochs.length; i++) {
    while (currentEpoch < sortedEpochs[i]) {
      missingEpochs.push(currentEpoch);
      currentEpoch += oneDayInMillis; // Move to the next day
    }
    // Move to the next day after the current epoch
    currentEpoch += oneDayInMillis;
  }

  // Check if there are any missing days between the last epoch and the end date
  while (currentEpoch <= endEpoch) {
    missingEpochs.push(currentEpoch);
    currentEpoch += oneDayInMillis;
  }

  return missingEpochs;
};

export const findMissingDates = (dates: string[], startDate: string, endDate: string) => {
  const me = findMissingEpochs(
    dates.map((d) => new Date(d).getTime()),
    new Date(startDate).getTime(),
    new Date(endDate).getTime()
  );
  return me.map((d) => new Date(d).toISOString());
};

export const getRandomColor = () => {
  return `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, '0')}`;
};

/**
 * Generates a deterministic color from an advertiser ID using a hash-based approach.
 * Uses the golden ratio to distribute hues evenly across the color wheel,
 * ensuring that even similar IDs produce visually distinct colors.
 */
export const getColorFromAdvertiserId = (advertiserId: string): string => {
  // Weight each character by position with prime numbers for better distribution
  const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37];
  let hash = 0;

  for (let i = 0; i < advertiserId.length; i++) {
    const char = advertiserId.charCodeAt(i);
    const weight = primes[i % primes.length];
    hash = (hash * weight + char) | 0;
  }

  const goldenRatio = 0.618033988749895;
  const h = Math.floor(((Math.abs(hash) * goldenRatio) % 1) * 360);
  const s = 55 + (Math.abs(hash >> 8) % 40); // 55-95% saturation
  const l = 35 + (Math.abs(hash >> 16) % 50); // 35-85% lightness

  return hslToHex(h, s, l);
};

/**
 * Converts HSL color values to hex format
 */
const hslToHex = (h: number, s: number, l: number): string => {
  const sDecimal = s / 100;
  const lDecimal = l / 100;

  const c = (1 - Math.abs(2 * lDecimal - 1)) * sDecimal;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lDecimal - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const rHex = Math.round((r + m) * 255)
    .toString(16)
    .padStart(2, '0');
  const gHex = Math.round((g + m) * 255)
    .toString(16)
    .padStart(2, '0');
  const bHex = Math.round((b + m) * 255)
    .toString(16)
    .padStart(2, '0');

  return `#${rHex}${gHex}${bHex}`;
};

export const hexColorForAudience = (audience: Audience, colorOverrides: Record<string, string>, campaignEntityColors?: Record<string, string>): undefined | string => {
  // Priority: colorOverrides (selected) > campaignEntityColors > undefined
  if (colorOverrides[audience.advertiser_id]) {
    return colorOverrides[audience.advertiser_id];
  }
  if (campaignEntityColors && campaignEntityColors[audience.advertiser_id]) {
    return campaignEntityColors[audience.advertiser_id];
  }
  return undefined;
};

export const isBboxWithin = (bbox1: MapBounds, bbox2: number[]) => {
  if (!bbox1) return false;
  // Returns a (minx, miny, maxx, maxy) tuple (float values) that bounds the object.
  return bbox1.southWest.lat >= bbox2[1] && bbox1.southWest.lng >= bbox2[0] && bbox1.northEast.lat <= bbox2[3] && bbox1.northEast.lng <= bbox2[2];
};

export const mapBoundsWKT = (mapBounds: MapBounds) => {
  const neLat = mapBounds?.northEast.lat;
  const neLng = mapBounds?.northEast.lng;
  const swLat = mapBounds?.southWest.lat;
  const swLng = mapBounds?.southWest.lng;
  return 'POLYGON((' + swLng + ' ' + swLat + ',' + swLng + ' ' + neLat + ',' + neLng + ' ' + neLat + ',' + neLng + ' ' + swLat + ',' + swLng + ' ' + swLat + '))';
};

export const formatWKTPolygon = (startLnglat: LngLat, currentLnglat: LngLat) => {
  const { lat: startLat, lng: startLng } = startLnglat;
  const { lat: currentLat, lng: currentLng } = currentLnglat;

  // Determine the rectangle's boundaries
  const minLat = Math.min(startLat, currentLat); // Bottom
  const maxLat = Math.max(startLat, currentLat); // Top
  const minLng = Math.min(startLng, currentLng); // Left
  const maxLng = Math.max(startLng, currentLng); // Right

  // Define the rectangle's corners
  const polygonCoordinates = [
    `${minLng} ${minLat}`, // Bottom-left corner
    `${minLng} ${maxLat}`, // Top-left corner
    `${maxLng} ${maxLat}`, // Top-right corner
    `${maxLng} ${minLat}`, // Bottom-right corner
    `${minLng} ${minLat}` // Close the polygon by repeating the first point
  ];

  // Format as WKT
  const wktPolygon = `POLYGON((${polygonCoordinates.join(', ')}))`;
  return wktPolygon;
};

/**
 * Convert an array of LngLat coordinates to WKT polygon format
 */
export const coordsToWKTPolygon = (coords: LngLat[]): string => {
  if (coords.length < 3) {
    throw new Error('At least 3 coordinates are required to form a polygon');
  }

  // Convert coordinates to WKT format and close the polygon
  const wktCoords = coords.map((c) => `${c.lng} ${c.lat}`);
  // Close the polygon by adding the first coordinate at the end
  wktCoords.push(`${coords[0].lng} ${coords[0].lat}`);

  return `POLYGON((${wktCoords.join(', ')}))`;
};

export const calculateFeatureArea = (feature: Feature) => {
  const featureCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: [feature]
  };

  const squareMeters = area(featureCollection);
  const squareKilometers = squareMeters / 1_000_000;
  return Math.round(squareKilometers);
};

/**
 * Calculate area in km² from an array of LngLat coordinates
 */
export const calculatePolygonArea = (coords: LngLat[]): number => {
  if (coords.length < 3) return 0;

  const coordinates = [...coords.map((c) => [c.lng, c.lat]), [coords[0].lng, coords[0].lat]];
  const feature: Feature = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates]
    }
  };

  return calculateFeatureArea(feature);
};

export const formatPred = (input: undefined | null | number | string) => {
  if (input === undefined || input === null) {
    return null;
  }
  const value = Number(input);
  if (!Number.isFinite(value)) return null;
  if (value >= 1) return '100%';
  if (value > 0 && value < 0.001) return '< 0.1%';

  // Truncate to one decimal place rather than round, so 0.9986 shows 99.8% and never rounds up to a
  // higher score. Cutting the decimal string (toFixed avoids exponent notation) keeps float error from
  // dropping a digit, e.g. 0.29 * 1000 is 289.99999999999994.
  const [whole, fraction] = value.toFixed(10).split('.');
  return `${(Number(whole + fraction.slice(0, 3)) / 10).toFixed(1)}%`;
};

export const orderedObjectString = (obj: Record<string, unknown>): string => {
  const entries = Object.entries(obj).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
};

export const formatToShortNumber = (num: number, decimals: number = 1): string => {
  if (num < 1_000) {
    return num.toString(); // Return the number as-is if less than 1,000
  }

  const suffixes = ['K', 'M'];
  const factor = Math.floor((num.toString().length - 1) / 3);
  const shortNumber = num / Math.pow(1_000, factor);
  const formattedNumber = shortNumber.toFixed(decimals);

  return `${formattedNumber.replace(/\.0+$/, '')}${suffixes[factor - 1]}`;
};

export const formatNumber = (num: number, decimals: number = 1): string => {
  if (num < 1000) return num.toString(); // No formatting for numbers < 1000

  const suffixes = ['', 'K', 'M', 'B', 'T', 'Q', 'Qi'];
  const tier = Math.floor(Math.log10(Math.abs(num)) / 3); // Determines the suffix index

  if (tier === 0) return num.toString(); // Safety check
  if (tier >= suffixes.length) return num.toExponential(decimals); // Fallback for extremely large numbers

  const scaled = num / Math.pow(10, tier * 3); // Scale the number
  const rounded = scaled.toFixed(decimals); // Round to specified decimal places

  return `${rounded.replace(/\.0$/, '')}${suffixes[tier]}`;
};

export const extractNameFromEmail = (email: string): string => {
  const name = email.split('@')[0];
  // capitalize the first letter of each part of the name
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const extractInitialsFromEmail = (email: string) => {
  // Get the part before @
  const localPart = email.split('@')[0];

  // Split on dot, underscore, or dash
  const parts = localPart.split(/[._-]+/);

  if (parts.length >= 2) {
    const firstInitial = parts[0][0];
    const lastInitial = parts[1][0];
    return (firstInitial + lastInitial).toUpperCase();
  } else if (parts.length === 1) {
    // Fallback if only one name
    return parts[0].slice(0, 2).toUpperCase(); // e.g., "ar" from "arni"
  }

  return null;
};

export const toDayjs = (value: unknown): Dayjs | null => {
  if (dayjs.isDayjs(value)) return value;

  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }

  return null;
};

export const centerEllipsis = (text: string | number, maxLength: number = 19): string => {
  const str = String(text);
  if (str.length <= maxLength) return str;

  const ellipsis = '...';
  const charsToShow = maxLength - ellipsis.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return str.substring(0, frontChars) + ellipsis + str.substring(str.length - backChars);
};

/**
 * Formats a manufacturer address into a structured multi-line format
 * Attempts to parse various address components like building, street, district, city, province, country, postal code
 */
export const formatManufacturerAddress = (address: string): string[] => {
  if (!address || typeof address !== 'string') return ['N/A'];

  const trimmedAddress = address.trim();
  if (!trimmedAddress) return ['N/A'];

  // Common patterns to help split the address
  const lines: string[] = [];

  // Split by common delimiters and clean up
  const parts = trimmedAddress
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  // Identify country and postal code (usually at the end)
  const lastPart = parts[parts.length - 1];
  // Safe, linear-time parser for country/province/postal code
  let countryPostalMatch: null | [string, string, string] = null;
  const trimmed = lastPart.trim();
  // Try to find a 2-letter country code and a 5-7 digit postal code at the end
  const postalCodeMatch = trimmed.match(/([A-Z]{2})\s*(\d{5,7})\s*$/);
  if (postalCodeMatch) {
    const country = postalCodeMatch[1];
    const postalCode = postalCodeMatch[2];
    // Everything before the country code is city/province
    const cityProvince = trimmed.slice(0, postalCodeMatch.index).trim();
    countryPostalMatch = [cityProvince, country, postalCode];
  }

  if (countryPostalMatch) {
    const [cityProvince, country, postalCode] = countryPostalMatch;
    // Remove the last part since we'll reconstruct it
    const addressParts = parts.slice(0, -1);

    // Add building/room information (usually first 1-2 parts)
    if (addressParts.length > 0) {
      lines.push(addressParts[0]);
    }

    // Add street/area information (middle parts)
    if (addressParts.length > 1) {
      lines.push(addressParts.slice(1).join(', '));
    }

    // Add city, province, country and postal code
    lines.push(`${cityProvince} ${country} ${postalCode}`);
  } else {
    // Fallback: group into logical chunks
    if (parts.length <= 2) {
      lines.push(...parts);
    } else {
      // First part: building/room
      lines.push(parts[0]);

      // Middle parts: street and area info
      if (parts.length > 2) {
        lines.push(parts.slice(1, -1).join(', '));
      }

      // Last part: city/country/postal
      lines.push(parts[parts.length - 1]);
    }
  }

  return lines.filter((line) => line.length > 0);
};

export const createZoomHandler = (xDataRef: { current: Date[] }, startRef: { current: HTMLSpanElement | null }, endRef: { current: HTMLSpanElement | null }) => {
  return (zoomData: { axisId: unknown; start: number; end: number }[]) => {
    const xZoom = zoomData[0];
    if (!xZoom) return;

    const xData = xDataRef.current;
    if (!xData.length) return;

    const n = xData.length;
    const lastIdx = n - 1;
    const startIdx = Math.min(Math.floor((xZoom.start / 100) * n), lastIdx);
    const endIdx = Math.max(Math.ceil((xZoom.end / 100) * n) - 1, 0);
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    if (startRef.current) startRef.current.textContent = fmt(xData[startIdx]);
    if (endRef.current) endRef.current.textContent = fmt(xData[endIdx]);
  };
};

export const createTimeZoomHandler = (xDataRef: { current: Date[] }, startRef: { current: HTMLSpanElement | null }, endRef: { current: HTMLSpanElement | null }) => {
  return (zoomData: { axisId: unknown; start: number; end: number }[]) => {
    const xZoom = zoomData[0];
    if (!xZoom) return;

    const xData = xDataRef.current;
    if (!xData.length) return;

    const minTime = xData[0].getTime();
    const maxTime = xData[xData.length - 1].getTime();
    const fmt = (t: number) => new Date(t).toISOString().split('T')[0];

    if (startRef.current) startRef.current.textContent = fmt(minTime + (xZoom.start / 100) * (maxTime - minTime));
    if (endRef.current) endRef.current.textContent = fmt(minTime + (xZoom.end / 100) * (maxTime - minTime));
  };
};

/**
 * Gets or creates a daily session ID that persists in localStorage for the current day
 * If no session exists or it's from a previous day, creates a new one
 */
export const getDailySessionId = (): string => {
  const today = dayjs().format('YYYY-MM-DD');
  const storageKey = 'daily-session-id';
  const storageDateKey = 'daily-session-date';

  try {
    const storedDate = localStorage.getItem(storageDateKey);
    const storedSessionId = localStorage.getItem(storageKey);

    // If we have a stored session from today, use it
    if (storedDate === today && storedSessionId) {
      return storedSessionId;
    }

    // Otherwise, create a new session ID for today
    const newSessionId = uuidv4();

    localStorage.setItem(storageKey, newSessionId);
    localStorage.setItem(storageDateKey, today);

    return newSessionId;
  } catch {
    // Fallback to regular UUID if localStorage is not available
    return uuidv4();
  }
};
